/**
 * Lista capitolati.
 *
 * Il capitolato **è la versione, non la serie**: «Pulizie v2» è un record distinto, legato a v1
 * da `capitolatoPrecedente`. Non esiste un record padre sopra le versioni, quindi la lista mostra
 * righe per versione e la colonna «versione» va letta insieme al nome.
 *
 * Un capitolato può coprire più tipologie (servizi): la colonna le elenca, principale in testa, e
 * il filtro per tipologia trova anche i capitolati che la coprono senza averla come principale.
 * Il filtro «solo attivi» risponde a *quale capitolato uso oggi*: al massimo uno per serie di
 * versioni, ma capitolati diversi sulla stessa tipologia possono essere attivi insieme (ADR-0006).
 *
 * Un capitolato **master** raggruppa dei capitolati semplici (i sub) e non sceglie tipologie: le
 * eredita dai sub. Il tipo si sceglie qui, alla creazione, e non si cambia più (ADR-0007).
 */

import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import { useToast } from "@shared/components/ui/Toast";
import { capitolatiApi, anagraficheApi } from "../services/acquistiApi";
import {
  Campo,
  Testo,
  Scelta,
  Interruttore,
  Caricamento,
  Vuoto,
  Avviso,
  SceltaMultipla,
} from "../components/campi";
import {
  STATO_CAPITOLATO,
  STATO_CAPITOLATO_BADGE,
  TIPO_CAPITOLATO,
  CAPITOLATO_SEMPLICE,
  CAPITOLATO_MASTER,
  opzioni,
} from "../constants/optionSet";
import { dataIt, etichetta } from "../utils/formato";

export default function CapitolatiPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { canPerformAction } = useAuth();
  const puoScrivere = canPerformAction("capitolati.gestisci");

  const [filtri, setFiltri] = useState({
    search: "",
    stato: "",
    tipologiaId: "",
    soloAttivi: false,
  });
  const [capitolati, setCapitolati] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState(null);
  const [creazione, setCreazione] = useState(null);

  useEffect(() => {
    anagraficheApi
      .tipologieFornitura()
      .then((r) => setTipologie(r.value || []))
      .catch(() => {});
  }, []);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      const res = await capitolatiApi.list({
        ...filtri,
        soloAttivi: filtri.soloAttivi ? "true" : "",
      });
      setCapitolati(res.value || []);
    } catch (e) {
      setErrore(e.message || "Impossibile caricare i capitolati");
    } finally {
      setCaricamento(false);
    }
  }, [filtri]);

  useEffect(() => {
    const t = setTimeout(carica, 300);
    return () => clearTimeout(t);
  }, [carica]);

  const set = (chiave) => (valore) =>
    setFiltri((f) => ({ ...f, [chiave]: valore }));

  const crea = async (e) => {
    e.preventDefault();
    try {
      const master = Number(creazione.tipo) === CAPITOLATO_MASTER;
      if (!master && !creazione.tipologieIds.length) {
        setErrore("Scegli almeno una tipologia di fornitura");
        return;
      }
      const res = await capitolatiApi.create(
        master
          ? { nome: creazione.nome, tipo: CAPITOLATO_MASTER }
          : { ...creazione, tipo: CAPITOLATO_SEMPLICE },
      );
      showToast("Capitolato creato in bozza", "success");
      navigate(`/capitolati/${res.id}`);
    } catch (err) {
      setErrore(err.data?.message || err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">Capitolati</h1>
        <span className="text-sm text-gray-500">
          {caricamento ? "…" : `${capitolati.length} versioni`}
        </span>
        {puoScrivere && (
          <button
            type="button"
            className="btn-primary ml-auto"
            onClick={() =>
              setCreazione(
                creazione
                  ? null
                  : { nome: "", tipo: CAPITOLATO_SEMPLICE, tipologieIds: [] },
              )
            }
          >
            {creazione ? "Annulla" : "+ Nuovo capitolato"}
          </button>
        )}
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {creazione && (
        <form onSubmit={crea} className="card space-y-4">
          <h2 className="font-semibold text-gray-900">Nuovo capitolato</h2>
          <p className="text-sm text-gray-600">
            Nasce sempre in <strong>Bozza</strong>, e la versione la assegna il
            server. Diventerà attivabile quando avrà almeno una voce.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Campo label="Nome *">
              <Testo
                valore={creazione.nome}
                onChange={(v) => setCreazione((c) => ({ ...c, nome: v }))}
                required
              />
            </Campo>
            <Campo
              label="Tipo *"
              aiuto={
                Number(creazione.tipo) === CAPITOLATO_MASTER
                  ? "Raggruppa più capitolati semplici (i sub), più voci proprie del pacchetto. Le tipologie le eredita dai sub."
                  : "Un capitolato tecnico: si usa da solo in gara, o come sub dentro un master."
              }
            >
              <Scelta
                valore={creazione.tipo}
                onChange={(v) =>
                  setCreazione((c) => ({ ...c, tipo: Number(v) }))
                }
                opzioni={opzioni(TIPO_CAPITOLATO)}
              />
            </Campo>
            {Number(creazione.tipo) !== CAPITOLATO_MASTER && (
              <Campo
                label="Tipologie di fornitura (servizi) *"
                className="md:col-span-2"
                aiuto="Una o più: un capitolato può valere per più servizi insieme. Si cambiano solo finché resta in Bozza."
              >
                <SceltaMultipla
                  valori={creazione.tipologieIds}
                  onChange={(v) =>
                    setCreazione((c) => ({ ...c, tipologieIds: v }))
                  }
                  opzioni={tipologie.map((t) => ({
                    value: t.id,
                    label: t.nome,
                  }))}
                />
              </Campo>
            )}
          </div>
          <button type="submit" className="btn-primary">
            Crea in bozza
          </button>
        </form>
      )}

      <div className="card !p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <Campo label="Cerca">
            <Testo
              valore={filtri.search}
              onChange={set("search")}
              placeholder="nome del capitolato"
            />
          </Campo>
          <Campo label="Stato">
            <Scelta
              valore={filtri.stato}
              onChange={set("stato")}
              opzioni={opzioni(STATO_CAPITOLATO)}
              vuoto="tutti"
            />
          </Campo>
          <Campo label="Tipologia">
            <Scelta
              valore={filtri.tipologiaId}
              onChange={set("tipologiaId")}
              opzioni={tipologie.map((t) => ({ value: t.id, label: t.nome }))}
              vuoto="tutte"
            />
          </Campo>
          <div className="pb-2">
            <Interruttore
              valore={filtri.soloAttivi}
              onChange={set("soloAttivi")}
              testo="Solo quelli in vigore"
            />
          </div>
        </div>
      </div>

      <div className="card !p-0 overflow-x-auto">
        {caricamento ? (
          <Caricamento />
        ) : capitolati.length === 0 ? (
          <Vuoto titolo="Nessun capitolato">
            Un capitolato definisce cosa si chiede e su cosa si valuta, per una
            o più tipologie di fornitura.
          </Vuoto>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Capitolato</th>
                <th>Tipologie</th>
                <th className="text-center">Versione</th>
                <th>Stato</th>
                <th>Attivato</th>
                <th>Blocco</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {capitolati.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link
                      to={`/capitolati/${c.id}`}
                      className="text-acquisti-700 hover:underline font-medium"
                    >
                      {c.nome || "(senza nome)"}
                    </Link>
                    {c.master && (
                      <span className="badge-info ml-2">master</span>
                    )}
                  </td>
                  <td>
                    {(c.tipologieCoperte || []).map((t) => t.nome).join(", ") ||
                      c.tipologiaFornitura?.nome ||
                      "—"}
                  </td>
                  <td className="text-center">v{c.versione ?? "?"}</td>
                  <td>
                    <span
                      className={
                        STATO_CAPITOLATO_BADGE[c.stato] || "badge-gray"
                      }
                    >
                      {c.statoLabel || etichetta(STATO_CAPITOLATO, c.stato)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap">
                    {dataIt(c.dataAttivazione)}
                  </td>
                  <td>
                    {c.bloccato ? (
                      <span
                        className="badge-danger"
                        title={c.motivoBlocco || undefined}
                      >
                        bloccato
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
